import React, { useEffect, useMemo, useState } from "react";
import { findAllPurchase } from "../../../../api/inventoryService";
import { useNavigate } from "react-router-dom";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import EventIcon from "@mui/icons-material/Event";
import BottomTab from "../../../../components/pagination/BottomTab";
import {
  CardSkeleton,
  TableSkeleton,
} from "../../../../components/skeleton/ListSkeleton";
import {
  DUE_FILTERS,
  DueFilter,
  PURCHASE_PAGE_SIZE,
  dueTone,
  filterPurchases,
  formatAmount,
  invoiceDateLabel,
  pageSlice,
} from "../../purchaseListUtils";

const DUE_STYLE: Record<string, string> = {
  owed: "bg-red-50 dark:bg-red-900/40 text-red-600 dark:text-red-300",
  settled: "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300",
  overpaid: "bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
};

function AllPurchase() {
  const [data, setdata] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [due, setDue] = useState<DueFilter>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    findAllPurchase()
      .then((data) => setdata(data as any[]))
      .catch((error) => console.error(error?.response))
      .finally(() => setLoading(false));
  }, []);

  const filteredData = useMemo(
    () => filterPurchases(data, { search, due, from, to }),
    [data, search, due, from, to]
  );

  // BottomTab keeps its own page number, so it and the slice are reset
  // together whenever the filters change what is being paged through.
  const filterKey = `${search}|${due}|${from}|${to}`;
  useEffect(() => setPage(1), [filterKey]);

  // The reset effect lands a render late, so the page is clamped here too -
  // a filter that shrinks the list must not flash an empty table first.
  const lastPage = Math.max(1, Math.ceil(filteredData.length / PURCHASE_PAGE_SIZE));
  const pageRows = pageSlice(filteredData, Math.min(page, lastPage));
  const hasFilters = Boolean(search || from || to) || due !== "all";
  const showSkeleton = loading && data.length === 0;

  const clearFilters = () => {
    setSearch("");
    setDue("all");
    setFrom("");
    setTo("");
  };

  return (
    <>
      {/* Header Card */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3 mb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5">
          <div className="relative flex-1 max-w-md">
            <SearchIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
              sx={{ fontSize: 16 }}
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search supplier or invoice no..."
              className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-50 dark:bg-cyan-900/40 rounded-lg shrink-0">
            <ShoppingCartIcon className="text-cyan-500 dark:text-cyan-300" sx={{ fontSize: 16 }} />
            <span className="font-bold text-xs text-cyan-600 dark:text-cyan-300">
              {loading ? "…" : filteredData.length}
            </span>
            <span className="text-slate-500 dark:text-slate-400 text-[10px]">
              {hasFilters ? `of ${data.length} Purchases` : "Purchases"}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {DUE_FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => setDue(filter.value)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                due === filter.value
                  ? "bg-gradient-to-r from-primary to-primaryLight text-white shadow-md shadow-primary/20"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              {filter.label}
            </button>
          ))}

          <div className="flex items-center gap-1.5 ml-auto">
            <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-2 py-1.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-[11px] text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-2 py-1.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-[11px] text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-[11px] font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        {/* Mobile Cards */}
        <div className="md:hidden p-3 space-y-2">
          {showSkeleton ? (
            <CardSkeleton cards={4} height="h-28" />
          ) : pageRows.length === 0 ? (
            <div className="text-center py-8 text-slate-400 dark:text-slate-500">
              <ShoppingCartIcon sx={{ fontSize: 36 }} className="mb-2" />
              <p className="text-xs">No purchases found</p>
            </div>
          ) : (
            pageRows.map((item: any, i: number) => (
              <div key={i} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 border border-slate-100 dark:border-slate-600">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-xs text-slate-800 dark:text-slate-100">{item?.supplier}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                      {item?.invoiceNo ? `Invoice ${item.invoiceNo}` : "No invoice no."}
                    </p>
                    <div
                      className="flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400 mt-0.5"
                      title={invoiceDateLabel(item).fallback ? "No invoice date - showing when it was entered" : "Invoice date"}
                    >
                      <EventIcon sx={{ fontSize: 12 }} />
                      {invoiceDateLabel(item).date}
                    </div>
                  </div>
                  <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-medium ${DUE_STYLE[dueTone(item?.due)]}`}>
                    {dueTone(item?.due) === "owed"
                      ? `Due: ${formatAmount(item?.due)}`
                      : dueTone(item?.due) === "overpaid"
                        ? `Overpaid: ${formatAmount(Math.abs(Number(item?.due) || 0))}`
                        : "Paid"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5 mb-2">
                  <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Total</p>
                    <p className="font-semibold text-xs text-slate-700 dark:text-slate-200">{formatAmount(item?.totalPrice)}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Cash</p>
                    <p className="font-semibold text-xs text-emerald-600 dark:text-emerald-300">{formatAmount(item?.paidCash)}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Card</p>
                    <p className="font-semibold text-xs text-blue-600 dark:text-blue-300">{formatAmount(item?.paidCard)}</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/client/inventory/purchase/edit-purchase/${item._id}`)}
                  className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all text-xs font-medium"
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                  Edit
                </button>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Invoice No</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Supplier</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Total</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Discount</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Cash</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Card</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Due</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Date</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                {showSkeleton ? (
                  <TableSkeleton rows={6} colSpan={9} />
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-slate-400 dark:text-slate-500">
                      <ShoppingCartIcon sx={{ fontSize: 36 }} className="mb-2" />
                      <p className="text-xs">No purchases found</p>
                    </td>
                  </tr>
                ) : (
                  pageRows.map((item: any, i: number) => (
                    <tr key={i} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                          {item?.invoiceNo || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-medium text-xs text-slate-800 dark:text-slate-100">{item?.supplier}</span>
                      </td>
                      <td className="px-3 py-2 font-semibold text-xs text-slate-700 dark:text-slate-200">{formatAmount(item?.totalPrice)}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{formatAmount(item?.discount)}</td>
                      <td className="px-3 py-2">
                        <span className="text-emerald-600 dark:text-emerald-300 font-medium text-xs">{formatAmount(item?.paidCash)}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-blue-600 dark:text-blue-300 font-medium text-xs">{formatAmount(item?.paidCard)}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${DUE_STYLE[dueTone(item?.due)]}`}
                          title={dueTone(item?.due) === "overpaid" ? "Paid more than the invoice total" : ""}
                        >
                          {formatAmount(item?.due)}
                        </span>
                      </td>
                      <td
                        className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400"
                        title={invoiceDateLabel(item).fallback ? "No invoice date - showing when it was entered" : "Invoice date"}
                      >
                        {invoiceDateLabel(item).date}
                        {invoiceDateLabel(item).fallback && (
                          <span className="text-slate-300 dark:text-slate-500"> *</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => navigate(`/client/inventory/purchase/edit-purchase/${item._id}`)}
                          className="p-1.5 rounded-md bg-cyan-50 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-900/60 transition-colors"
                          title="Edit"
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="border-t border-slate-100 dark:border-slate-700">
          <BottomTab
            key={filterKey}
            title="Purchases"
            count={filteredData.length}
            onPageChange={setPage}
            pageSize={PURCHASE_PAGE_SIZE}
          />
        </div>
      </div>
    </>
  );
}

export default AllPurchase;
